// src/services/dispositivos.js

import AuthService from "../auth/auth.js";
import { sensorMap } from "../utils/sensores.js";

/**
 * Servicio para manejar todas las operaciones relacionadas con dispositivos
 * Conecta con el endpoint /dispositivos/api/dispositivos/ del backend
 * VERSIÓN COMPLETA CON SOPORTE DE IMÁGENES Y CAMPO EN_LINEA
 */
class DispositivosService {
  constructor() {
    // URL base del backend
    this.baseURL = process.env.VUE_APP_API_URL || "https://efibox.efintel.cl";
    this.endpoint = "/dispositivos/api/dispositivos/";

    // NUEVO: Endpoint para detalles de levantamiento
    this.endpointLevantamiento = "/dispositivos/api/detalles-levantamiento/";

    // Configuración para manejo de imágenes
    this.maxImageSize = 5 * 1024 * 1024; // 5MB
    this.allowedImageTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
  }

  // ==========================================
  // CONSTANTES Y ENUMS
  // ==========================================

  get COLORES_OPERATIVIDAD() {
    return ["VERDE", "AMARILLO", "NARANJA", "ROJO", "GRIS"];
  }

  get PORCENTAJES_OPERATIVIDAD() {
    return [100, 50, 0];
  }

  // ==========================================
  // MÉTODOS DE LECTURA (GET)
  // ==========================================

  /**
   * Obtiene todos los dispositivos del backend
   * @returns {Promise<Array>} Array de dispositivos mapeados para el frontend
   */
  async obtenerTodosLosDispositivos() {
    try {
      const response = await AuthService.authenticatedRequest(this.endpoint);

      // Manejo robusto de la respuesta
      let dispositivos = [];

      if (Array.isArray(response)) {
        dispositivos = response;
      } else if (response && Array.isArray(response.results)) {
        dispositivos = response.results;
      } else if (response && Array.isArray(response.data)) {
        dispositivos = response.data;
      } else if (response && Array.isArray(response.dispositivos)) {
        dispositivos = response.dispositivos;
      } else {
        dispositivos = [];
      }

      // Mapear datos
      const dispositivosMapeados = dispositivos.map((dispositivo, index) => {
        try {
          return this.mapearDispositivoBackendAFrontend(dispositivo);
        } catch (error) {
          return this.crearDispositivoFallback(dispositivo, index);
        }
      });

      return dispositivosMapeados;
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  /**
   * Obtiene dispositivos filtrados por local_id
   * @param {number} localId - ID del local
   * @returns {Promise<Array>} Array de dispositivos del local específico
   */
  async obtenerDispositivosPorLocal(localId) {
    try {
      const response = await AuthService.authenticatedRequest(this.endpoint);

      let dispositivos = [];

      if (Array.isArray(response)) {
        dispositivos = response;
      } else if (response && Array.isArray(response.results)) {
        dispositivos = response.results;
      } else {
        dispositivos = [];
      }

      // Filtrar por local_id
      const dispositivosDelLocal = dispositivos.filter((dispositivo) => {
        return dispositivo.local_id === localId || dispositivo.local_id === parseInt(localId);
      });

      // Mapear dispositivos
      const dispositivosMapeados = dispositivosDelLocal.map((dispositivo, index) => {
        try {
          const dispositivoMapeado = this.mapearDispositivoBackendAFrontend(dispositivo);
          dispositivoMapeado.local_id = dispositivo.local_id;
          return dispositivoMapeado;
        } catch (error) {
          return this.crearDispositivoFallback(dispositivo, index);
        }
      });

      return dispositivosMapeados;
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  /**
   * Obtiene un dispositivo específico por ID
   * @param {number} id - ID del dispositivo
   * @returns {Promise<Object>} Dispositivo mapeado para el frontend
   */
  async obtenerDispositivoPorId(id) {
    try {
      const response = await AuthService.authenticatedRequest(`${this.endpoint}${id}/`);
      return this.mapearDispositivoBackendAFrontend(response);
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  /**
   * Obtiene el listado de detalles de levantamiento
   * GET /dispositivos/api/detalles-levantamiento/
   * @returns {Promise<Array>} Lista de detalles de levantamiento
   */
  async obtenerDetallesLevantamiento() {
    try {
      const response = await AuthService.authenticatedRequest(this.endpointLevantamiento);

      let detalles = [];

      if (Array.isArray(response)) {
        detalles = response;
      } else if (response && Array.isArray(response.results)) {
        detalles = response.results;
      } else {
        detalles = [];
      }

      return detalles;
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  async crearDetalleLevantamiento(datosDetalle) {
    const response = await AuthService.authenticatedRequest(this.endpointLevantamiento, {
      method: "POST",
      body: JSON.stringify(datosDetalle),
    });
    return response;
  }

  async actualizarDetalleLevantamiento(id, datosDetalle) {
    const response = await AuthService.authenticatedRequest(`${this.endpointLevantamiento}${id}/`, {
      method: "PATCH",
      body: JSON.stringify(datosDetalle),
    });
    return response;
  }

  // ==========================================
  // MÉTODOS DE CREACIÓN Y ACTUALIZACIÓN
  // ==========================================
  /**
   * Crea un nuevo dispositivo en el backend CON SOPORTE DE IMAGEN
   * @param {Object} datosDispositivo - Datos del nuevo dispositivo
   * @param {File|null} archivoImagen - Archivo de imagen opcional
   * @returns {Promise<Object>} Dispositivo creado y mapeado
   */
  async crearDispositivo(datosDispositivo, archivoImagen = null) {
    try {
      // Validaciones requeridas
      const camposRequeridos = ["nombre_dispositivo", "local_id"];
      const errores = [];

      camposRequeridos.forEach((campo) => {
        if (!datosDispositivo[campo]) {
          errores.push(`El campo ${campo.replace("_", " ")} es obligatorio`);
        }
      });

      if (errores.length > 0) {
        throw new Error(errores.join(". "));
      }

      // Validaciones adicionales
      if (datosDispositivo.nombre_dispositivo && datosDispositivo.nombre_dispositivo.length > 100) {
        throw new Error("El nombre no puede exceder 100 caracteres");
      }

      // Validar imagen si existe
      if (archivoImagen) {
        this.validarArchivoImagen(archivoImagen);
      }

      // Construir FormData
      const formData = this.construirFormData(datosDispositivo, archivoImagen);

      // Obtener tokens
      const tokens = this.obtenerTokens();

      // Realizar petición POST con FormData
      const response = await fetch(`${this.baseURL}${this.endpoint}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "X-CSRFToken": tokens.csrf || "",
          ...(tokens.jwt ? { Authorization: `Bearer ${tokens.jwt}` } : {}),
        },
        body: formData,
      });

      // Procesar respuesta
      const responseData = await this.procesarRespuesta(response);

      // Mapear y devolver el dispositivo creado
      return this.mapearDispositivoBackendAFrontend(responseData);
    } catch (error) {
      throw this.manejarError(error);
    }
  }
  /**
   * Elimina un dispositivo del backend
   * @param {number} dispositivoId - ID del dispositivo a eliminar
   * @returns {Promise<void>}
   */
  async eliminarDispositivo(dispositivoId) {
    try {
      const response = await AuthService.authenticatedRequest(`${this.endpoint}${dispositivoId}/`, { method: "DELETE" });

      return response;
    } catch (error) {
      throw this.manejarError(error);
    }
  }
  /**
   * Obtiene información necesaria para la eliminación de un dispositivo
   * @param {number} dispositivoId - ID del dispositivo
   * @returns {Promise<Object>} Información para validar eliminación
   */
  async obtenerInfoEliminacion(dispositivoId) {
    try {
      // Obtener el dispositivo completo
      const dispositivo = await this.obtenerDispositivoPorId(dispositivoId);

      // Por ahora, los dispositivos se pueden eliminar siempre
      return {
        dispositivo: dispositivo,
        puedeEliminar: true,
        razonNoEliminar: null,
        advertencias: [],
        dependencias: [],
      };
    } catch (error) {
      throw this.manejarError(error);
    }
  }
  /**
   * Actualiza un dispositivo existente CON SOPORTE DE IMAGEN
   * @param {number} dispositivoId - ID del dispositivo a actualizar
   * @param {Object} datosDispositivo - Datos actualizados del dispositivo
   * @param {File|null} archivoImagen - Nuevo archivo de imagen (opcional)
   * @param {boolean} eliminarImagenActual - Si se debe eliminar la imagen actual
   * @returns {Promise<Object>} Dispositivo actualizado y mapeado
   */
  async actualizarDispositivo(dispositivoId, datosDispositivo, archivoImagen = null, eliminarImagenActual = false) {
    try {
      // Validar imagen si existe
      if (archivoImagen) {
        this.validarArchivoImagen(archivoImagen);
      }

      // Construir FormData
      const formData = this.construirFormData(datosDispositivo, archivoImagen, eliminarImagenActual);

      // Obtener tokens
      const tokens = this.obtenerTokens();

      // Realizar petición PATCH
      const response = await fetch(`${this.baseURL}${this.endpoint}${dispositivoId}/`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "X-CSRFToken": tokens.csrf || "",
          ...(tokens.jwt ? { Authorization: `Bearer ${tokens.jwt}` } : {}),
        },
        body: formData,
      });

      // Procesar respuesta
      const responseData = await this.procesarRespuesta(response);

      return this.mapearDispositivoBackendAFrontend(responseData);
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  /**
   * Actualiza la configuración de un dispositivo
   * @param {number} id - ID del dispositivo
   * @param {Object} configuracion - Nueva configuración
   * @returns {Promise<Object>} Dispositivo actualizado
   */
  async actualizarConfiguracionDispositivo(id, configuracion) {
    try {
      const response = await AuthService.authenticatedRequest(`${this.endpoint}${id}/`, {
        method: "PATCH",
        body: JSON.stringify({
          configuracion_dispositivo: configuracion,
        }),
      });

      return this.mapearDispositivoBackendAFrontend(response);
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  /**
   * Cambia el estado activo/inactivo de un dispositivo
   * @param {number} id - ID del dispositivo
   * @param {boolean} activo - Nuevo estado (true/false)
   * @returns {Promise<Object>} Dispositivo actualizado
   */
  async cambiarEstadoDispositivo(id, activo) {
    try {
      const response = await AuthService.authenticatedRequest(`${this.endpoint}${id}/`, {
        method: "PATCH",
        body: JSON.stringify({
          activo_dispositivo: activo,
        }),
      });

      return this.mapearDispositivoBackendAFrontend(response);
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  /**
   * Actualiza el nombre de un dispositivo
   * @param {number} id - ID del dispositivo
   * @param {string} nuevoNombre - Nuevo nombre del dispositivo
   * @returns {Promise<Object>} Dispositivo actualizado
   */
  async actualizarNombreDispositivo(id, nuevoNombre) {
    try {
      // Validaciones
      if (!nuevoNombre || typeof nuevoNombre !== "string") {
        throw new Error("El nombre debe ser una cadena de texto válida");
      }

      const nombreLimpio = nuevoNombre.trim();
      if (nombreLimpio.length === 0) {
        throw new Error("El nombre no puede estar vacío");
      }

      if (nombreLimpio.length > 100) {
        throw new Error("El nombre no puede tener más de 100 caracteres");
      }

      const response = await AuthService.authenticatedRequest(`${this.endpoint}${id}/`, {
        method: "PATCH",
        body: JSON.stringify({
          nombre_dispositivo: nombreLimpio,
        }),
      });

      const dispositivoActualizado = this.mapearDispositivoBackendAFrontend(response);

      return dispositivoActualizado;
    } catch (error) {
      // Manejo específico de errores
      if (error.message?.includes("400")) {
        throw new Error("Datos inválidos. Verifica que el nombre sea correcto.");
      } else if (error.message?.includes("404")) {
        throw new Error("Dispositivo no encontrado");
      } else if (error.message?.includes("403")) {
        throw new Error("Sin permisos para modificar este dispositivo");
      }

      throw this.manejarError(error);
    }
  }

  /**
   * Actualiza la ubicación de un dispositivo
   * @param {number} id - ID del dispositivo
   * @param {string} nuevaUbicacion - Nueva ubicación del dispositivo
   * @returns {Promise<Object>} Dispositivo actualizado
   */
  async actualizarUbicacionDispositivo(id, nuevaUbicacion) {
    try {
      // Validaciones
      if (!nuevaUbicacion || typeof nuevaUbicacion !== "string") {
        throw new Error("La ubicación debe ser una cadena de texto válida");
      }

      const ubicacionLimpia = nuevaUbicacion.trim();
      if (ubicacionLimpia.length === 0) {
        throw new Error("La ubicación no puede estar vacía");
      }

      if (ubicacionLimpia.length > 150) {
        throw new Error("La ubicación no puede tener más de 150 caracteres");
      }

      const response = await AuthService.authenticatedRequest(`${this.endpoint}${id}/`, {
        method: "PATCH",
        body: JSON.stringify({
          ubicacion_dispositivo: ubicacionLimpia,
        }),
      });

      return this.mapearDispositivoBackendAFrontend(response);
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  // ==========================================
  // MÉTODOS HELPER PARA MANEJO DE IMÁGENES
  // ==========================================

  /**
   * Valida archivo de imagen
   * @param {File} archivo - Archivo a validar
   */
  validarArchivoImagen(archivo) {
    if (!archivo.type.startsWith("image/")) {
      throw new Error("El archivo debe ser una imagen válida (JPG, PNG, GIF)");
    }

    if (!this.allowedImageTypes.includes(archivo.type)) {
      throw new Error("Formato de imagen no soportado. Use JPG, PNG o GIF");
    }

    if (archivo.size > this.maxImageSize) {
      throw new Error("El archivo es muy grande. El tamaño máximo es 5MB");
    }
  }

  /**
   * Construye FormData para envío
   * @param {Object} datosDispositivo - Datos del dispositivo
   * @param {File|null} archivoImagen - Archivo de imagen opcional
   * @param {boolean} eliminarImagenActual - Si eliminar imagen actual
   * @returns {FormData} FormData preparado
   */
  construirFormData(datosDispositivo, archivoImagen = null, eliminarImagenActual = false) {
    const formData = new FormData();

    // Campos básicos requeridos
    if (datosDispositivo.nombre_dispositivo) {
      formData.append("nombre_dispositivo", datosDispositivo.nombre_dispositivo.trim());
    }

    if (datosDispositivo.local_id) {
      formData.append("local_id", parseInt(datosDispositivo.local_id));
    }

    // Campos opcionales
    if (datosDispositivo.modelo_dispositivo) {
      formData.append("modelo_dispositivo", datosDispositivo.modelo_dispositivo.trim());
    }

    if (datosDispositivo.ubicacion_dispositivo) {
      formData.append("ubicacion_dispositivo", datosDispositivo.ubicacion_dispositivo.trim());
    }

    // Nuevos campos agregados
    if (datosDispositivo.marca_dispositivo) {
      formData.append("marca_dispositivo", datosDispositivo.marca_dispositivo.trim());
    }

    if (datosDispositivo.capacidad_frio) {
      formData.append("capacidad_frio", datosDispositivo.capacidad_frio.trim());
    }

    if (datosDispositivo.capacidad_calor) {
      formData.append("capacidad_calor", datosDispositivo.capacidad_calor.trim());
    }

    if (datosDispositivo.refrigerante) {
      formData.append("refrigerante", datosDispositivo.refrigerante.trim());
    }

    if (datosDispositivo.carga) {
      formData.append("carga", datosDispositivo.carga.trim());
    }

    if (datosDispositivo.voltaje) {
      formData.append("voltaje", datosDispositivo.voltaje.trim());
    }

    if (datosDispositivo.corriente) {
      formData.append("corriente", datosDispositivo.corriente.trim());
    }

    // Tipo de dispositivo
    if (datosDispositivo.tipo_dispositivo) {
      formData.append("tipo_dispositivo", parseInt(datosDispositivo.tipo_dispositivo));
    }

    // Potencia nominal
    if (datosDispositivo.potencia_nominal) {
      formData.append("potencia_nominal", parseInt(datosDispositivo.potencia_nominal));
    }

    // Fecha instalación
    if (datosDispositivo.fecha_instalacion) {
      formData.append("fecha_instalacion", datosDispositivo.fecha_instalacion);
    }

    // Estado activo
    if (datosDispositivo.activo_dispositivo !== undefined) {
      formData.append("activo_dispositivo", Boolean(datosDispositivo.activo_dispositivo));
    }

    // Imagen por defecto
    if (datosDispositivo.imagen) {
      formData.append("imagen", datosDispositivo.imagen);
    }

    // Configuración del dispositivo (como JSON string)
    if (datosDispositivo.configuracion_dispositivo) {
      formData.append("configuracion_dispositivo", JSON.stringify(datosDispositivo.configuracion_dispositivo));
    }

    // Broker config (como JSON string)
    if (datosDispositivo.brokerConfig) {
      formData.append("broker_config", JSON.stringify(datosDispositivo.brokerConfig));
    }

    // Empresa ID si existe
    if (datosDispositivo.id_empresa) {
      formData.append("id_empresa", parseInt(datosDispositivo.id_empresa));
    }

    // MANEJO DE IMAGEN
    if (archivoImagen) {
      formData.append("media", archivoImagen);
    } else if (eliminarImagenActual) {
      formData.append("media", "");
    }

    return formData;
  }

  /**
   * Obtiene tokens de autenticación desde cookies
   * @returns {Object} Objeto con tokens csrf y jwt
   */
  obtenerTokens() {
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(";").shift();
      return null;
    };

    return {
      csrf: getCookie("csrftoken"),
      jwt: getCookie("iiotefintel_cookie"),
    };
  }

  /**
   * Procesa la respuesta HTTP y maneja errores
   * @param {Response} response - Respuesta fetch
   * @returns {Promise<Object>} Datos de respuesta parseados
   */
  async procesarRespuesta(response) {
    const responseText = await response.text();

    if (!response.ok) {
      let errorData;
      try {
        errorData = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(`Error del servidor (${response.status}): ${responseText}`);
      }

      // Construir mensaje de error detallado
      let mensajeError = "Error al procesar dispositivo:\n";

      if (errorData.nombre_dispositivo) {
        const errores = Array.isArray(errorData.nombre_dispositivo) ? errorData.nombre_dispositivo : [errorData.nombre_dispositivo];
        mensajeError += `• Nombre: ${errores.join(", ")}\n`;
      }

      if (errorData.local_id) {
        const errores = Array.isArray(errorData.local_id) ? errorData.local_id : [errorData.local_id];
        mensajeError += `• Local: ${errores.join(", ")}\n`;
      }

      if (errorData.media) {
        const errores = Array.isArray(errorData.media) ? errorData.media : [errorData.media];
        mensajeError += `• Imagen: ${errores.join(", ")}\n`;
      }

      if (errorData.non_field_errors) {
        const errores = Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors : [errorData.non_field_errors];
        mensajeError += `• ${errores.join(", ")}\n`;
      }

      throw new Error(mensajeError.trim() || `Error del servidor (${response.status})`);
    }

    // Parsear respuesta exitosa
    try {
      return JSON.parse(responseText);
    } catch (parseError) {
      throw new Error("Respuesta del servidor no válida");
    }
  }

  /**
   * Obtiene URL completa de imagen
   * @param {string} mediaPath - Ruta de la imagen
   * @returns {string|null} URL completa o null
   */
  obtenerUrlImagen(mediaPath) {
    if (!mediaPath) return null;

    if (mediaPath.startsWith("http")) {
      return mediaPath;
    }

    const cleanPath = mediaPath.startsWith("/") ? mediaPath : `/${mediaPath}`;
    return `${this.baseURL}${cleanPath}`;
  }

  // ==========================================
  // MÉTODOS DE MAPEO Y TRANSFORMACIÓN
  // ==========================================

  /**
   * Mapea un dispositivo del formato backend al formato frontend
   * CON PRESERVACIÓN DEL LOCAL_ID, FECHA_INSTALACION, NUEVOS CAMPOS Y EN_LINEA
   * @param {Object} dispositivoBackend - Dispositivo en formato backend
   * @returns {Object} Dispositivo en formato frontend
   */
  mapearDispositivoBackendAFrontend(dispositivoBackend) {
    if (!dispositivoBackend || typeof dispositivoBackend !== "object") {
      throw new Error("Dispositivo backend inválido");
    }

    // 1. GENERAMOS EL MAPA DE SENSORES NORMALIZADO (TODO EN MINÚSCULAS)
    const s = sensorMap(dispositivoBackend);

    // 2. CÁLCULO DE MODO (Usando minúsculas y alias recomendados)
    const modoRaw =
      s["modo"] ??
      s["mode"] ??
      s["estado"] ??
      dispositivoBackend.configuracion_dispositivo?.modo ??
      dispositivoBackend.configuracion_dispositivo?.Modo ??
      dispositivoBackend.modo_obtencion ??
      dispositivoBackend.modo ??
      0;

    let modoObtencion = "OFF";
    if (typeof modoRaw === "number" || !isNaN(Number(modoRaw))) {
      // Si llega 0, 1, 2, 3
      const mapeoNumerico = { 0: "OFF", 1: "FRIO", 2: "CALOR", 3: "VENTILACION" };
      modoObtencion = mapeoNumerico[Number(modoRaw)] || "OFF";
    } else if (typeof modoRaw === "string") {
      // Si llega texto
      const modoNormalizado = modoRaw.toUpperCase();
      if (modoNormalizado.includes("FRIO") || modoNormalizado.includes("FRÍO")) modoObtencion = "FRIO";
      else if (modoNormalizado.includes("CALOR") || modoNormalizado.includes("MANUAL") || modoNormalizado.includes("AUTO"))
        modoObtencion = "CALOR";
      else if (modoNormalizado.includes("VENT")) modoObtencion = "VENTILACION";
    }

    // 3. CÁLCULO DE DESHIELO (Usando minúsculas)
    const deshieloRaw =
      s["deshielo"] ??
      s["defrost"] ??
      dispositivoBackend.configuracion_dispositivo?.deshielo ??
      dispositivoBackend.configuracion_dispositivo?.Deshielo ??
      false;

    const deshieloActivo = String(deshieloRaw).toLowerCase() === "true" || deshieloRaw === true || deshieloRaw === 1 || deshieloRaw === "1";

    // 4. CÁLCULO DE OCCUPIED (Usando minúsculas)
    const occupiedRaw =
      s["occupied"] ??
      s["ocupado"] ??
      dispositivoBackend.configuracion_dispositivo?.occupied ??
      dispositivoBackend.configuracion_dispositivo?.Occupied ??
      false;

    const isOccupied = String(occupiedRaw).toLowerCase() === "true" || occupiedRaw === true || occupiedRaw === 1 || occupiedRaw === "1";

    // 5. CÁLCULO DE SETPOINT Y TEMPERATURAS PARA CONFIGURACION DE COMPATIBILIDAD
    // Notar que la clave normalizada de "Temp Inyección" es "temp inyeccion" (con espacio y sin tilde)
    const tempInyeccionRaw = s["temp inyeccion"] ?? dispositivoBackend.configuracion_dispositivo?.["Temp Inyeccion"] ?? 0;
    const tempRetornoRaw = s["temp retorno"] ?? dispositivoBackend.configuracion_dispositivo?.["Temp Retorno"] ?? 0;
    const setpointRaw = s["setpoint"] ?? dispositivoBackend.configuracion_dispositivo?.Setpoint ?? 22;

    // Estructura mapeada
    const dispositivoMapeado = {
      // Campos directos del backend
      id: dispositivoBackend.id || Date.now(),
      imagen: dispositivoBackend.media || "ImagenDefault",
      nombre_dispositivo: dispositivoBackend.nombre_dispositivo || "Dispositivo Sin Nombre",
      modelo_dispositivo: dispositivoBackend.modelo_dispositivo || "Modelo Desconocido",
      numero_serie: dispositivoBackend.numero_serie || "",
      mac_dispositivo: dispositivoBackend.mac_dispositivo || "",
      ubicacion_dispositivo: dispositivoBackend.ubicacion_dispositivo || "Ubicación Desconocida",
      activo_dispositivo: Boolean(dispositivoBackend.activo_dispositivo),
      en_linea: Boolean(dispositivoBackend.en_linea),
      fecha_instalacion: dispositivoBackend.fecha_instalacion || null,
      ultimo_mensaje: dispositivoBackend.ultimo_mensaje || null,

      // Nuevos campos técnicos
      marca_dispositivo: dispositivoBackend.marca_dispositivo || null,
      capacidad_frio: dispositivoBackend.capacidad_frio || null,
      capacidad_calor: dispositivoBackend.capacidad_calor || null,
      refrigerante: dispositivoBackend.refrigerante || null,
      carga: dispositivoBackend.carga || null,
      voltaje: dispositivoBackend.voltaje || null,
      corriente: dispositivoBackend.corriente || null,

      // Configuración del dispositivo (Usamos los valores calculados en tiempo real)
      configuracion_dispositivo: {
        Modo: modoRaw,
        Occupied: isOccupied,
        Setpoint: setpointRaw,
        "Temp Retorno": tempRetornoRaw,
        "Temp Inyeccion": tempInyeccionRaw,
        Deshielo: deshieloActivo,
        ...dispositivoBackend.configuracion_dispositivo,
      },

      // Campos de filtrado
      empresa_id: dispositivoBackend.empresa_id || 0,
      local_id: dispositivoBackend.local_id || null,
      tipo_dispositivo: dispositivoBackend.tipo_dispositivo || 1,

      // MODO Y ESTADOS SEPARADOS (Los que usan las tablas e iconos)
      modo_obtencion: modoObtencion,
      deshielo_activo: deshieloActivo,

      // Campos adicionales
      potencia_nominal: dispositivoBackend.potencia_nominal || 0,
      broker_config: dispositivoBackend.broker_config || null,
      media: dispositivoBackend.media || null,
      dispositivo_sensores: dispositivoBackend.dispositivo_sensores || [],

      // Campos simulados para compatibilidad
      bateria: this.simularBateria(),
      voltajes: this.simularVoltajes(),
      corrientes: this.simularCorrientes(),
      historialVoltajes: this.simularHistorialVoltajes(),
      alertas: this.simularAlertas(dispositivoBackend.id || 0),
    };

    return dispositivoMapeado;
  }

  /**
   * Crear dispositivo de fallback cuando hay error en el mapeo
   * @param {Object} dispositivoOriginal - Dispositivo original problemático
   * @param {number} index - Índice del dispositivo
   * @returns {Object} Dispositivo con estructura mínima
   */
  crearDispositivoFallback(dispositivoOriginal, index) {
    return {
      id: dispositivoOriginal?.id || `fallback_${index}`,
      imagen: "ImagenDefault",
      nombre_dispositivo: dispositivoOriginal?.nombre_dispositivo || `Dispositivo ${index + 1}`,
      modelo_dispositivo: dispositivoOriginal?.modelo_dispositivo || "Modelo Desconocido",
      numero_serie: "",
      mac_dispositivo: "",
      ubicacion_dispositivo: dispositivoOriginal?.ubicacion_dispositivo || "Ubicación Desconocida",
      activo_dispositivo: dispositivoOriginal?.activo_dispositivo || false,
      en_linea: dispositivoOriginal?.en_linea || false,
      fecha_instalacion: dispositivoOriginal?.fecha_instalacion || null,
      ultimo_mensaje: null,

      // Configuración por defecto consistente
      configuracion_dispositivo: {
        Modo: 0, // OFF por defecto
        Occupied: false,
        Setpoint: 22,
        "Temp Retorno": 0,
        "Temp Inyeccion": 0, // Sin acento
        Deshielo: false, // Campo de deshielo por defecto
      },

      empresa_id: dispositivoOriginal?.empresa_id || 0,
      local_id: dispositivoOriginal?.local_id || null,
      tipo_dispositivo: dispositivoOriginal?.tipo_dispositivo || 1,

      // Valores por defecto consistentes
      modo_obtencion: "OFF", // En lugar de 'AUTO'
      deshielo_activo: false, // Campo agregado en fallback

      potencia_nominal: dispositivoOriginal?.potencia_nominal || 0,
      media: dispositivoOriginal?.media || null,

      // Nuevos campos en fallback
      marca_dispositivo: dispositivoOriginal?.marca_dispositivo || null,
      capacidad_frio: dispositivoOriginal?.capacidad_frio || null,
      capacidad_calor: dispositivoOriginal?.capacidad_calor || null,
      refrigerante: dispositivoOriginal?.refrigerante || null,
      carga: dispositivoOriginal?.carga || null,
      voltaje: dispositivoOriginal?.voltaje || null,
      corriente: dispositivoOriginal?.corriente || null,
      dispositivo_sensores: dispositivoOriginal?.dispositivo_sensores || [],
      // Campos simulados
      bateria: 0,
      voltajes: {},
      corrientes: {},
      historialVoltajes: [],
      alertas: [],
    };
  }
  // ==========================================
  // MÉTODOS UTILITARIOS
  // ==========================================

  /**
   * Simula nivel de batería
   * @returns {number} Porcentaje de batería (0-100)
   */
  simularBateria() {
    return 85;
  }

  /**
   * Simula datos de voltajes para motores trifásicos
   * @returns {Object} Voltajes por fase
   */
  simularVoltajes() {
    return {
      fase1: 220.5,
      fase2: 218.3,
      fase3: 221.8,
      neutro: 2.1,
    };
  }

  /**
   * Simula datos de corrientes
   * @returns {Object} Corrientes por fase
   */
  simularCorrientes() {
    return {
      fase1: 15.2,
      fase2: 14.8,
      fase3: 15.5,
    };
  }

  /**
   * Simula historial de voltajes para gráficos
   * @returns {Array} Array de puntos de voltaje en el tiempo
   */
  simularHistorialVoltajes() {
    return [
      { tiempo: "00:00", f1: 220, f2: 218, f3: 221 },
      { tiempo: "02:00", f1: 219, f2: 217, f3: 220 },
      { tiempo: "04:00", f1: 221, f2: 219, f3: 222 },
      { tiempo: "06:00", f1: 220, f2: 218, f3: 221 },
      { tiempo: "08:00", f1: 222, f2: 220, f3: 223 },
      { tiempo: "10:00", f1: 221, f2: 219, f3: 222 },
      { tiempo: "12:00", f1: 220, f2: 218, f3: 221 },
      { tiempo: "14:00", f1: 219, f2: 217, f3: 220 },
    ];
  }

  /**
   * Simula alertas para el dispositivo
   * @param {number} dispositivoId - ID del dispositivo
   * @returns {Array} Array de alertas simuladas
   */
  simularAlertas(dispositivoId) {
    const alertasEjemplo = [
      {
        id: 1,
        tipo: "advertencia",
        mensaje: "Filtro requiere limpieza en 5 días",
        fecha: "18/06/2025 14:30",
      },
      {
        id: 2,
        tipo: "informacion",
        mensaje: "Calibración de sensores completada",
        fecha: "15/06/2025 09:15",
      },
    ];

    return dispositivoId === 2 ? alertasEjemplo : [];
  }

  /**
   * Maneja errores de las llamadas a la API
   * @param {Error} error - Error original
   * @returns {Error} Error procesado con mensaje amigable
   */
  manejarError(error) {
    let mensaje = "Error desconocido";
    let codigo = "UNKNOWN_ERROR";

    // Manejo específico para errores de AuthService
    if (error.message?.includes("Sesión expirada")) {
      mensaje = "Sesión expirada. Inicie sesión nuevamente.";
      codigo = "SESSION_EXPIRED";
    } else if (error.message?.includes("No hay sesión activa")) {
      mensaje = "No hay sesión activa. Inicie sesión.";
      codigo = "NO_SESSION";
    } else if (error.message?.includes("Error 401")) {
      mensaje = "No autorizado. Inicie sesión nuevamente.";
      codigo = "UNAUTHORIZED";
    } else if (error.message?.includes("Error 403")) {
      mensaje = "Sin permisos para gestionar dispositivos";
      codigo = "FORBIDDEN";
    } else if (error.message?.includes("Error 404")) {
      mensaje = "Dispositivo no encontrado";
      codigo = "NOT_FOUND";
    } else if (error.message?.includes("Error 500")) {
      mensaje = "Error interno del servidor";
      codigo = "SERVER_ERROR";
    } else if (error.message?.includes("Failed to fetch")) {
      mensaje = "Error de conexión. Verifique su internet.";
      codigo = "NETWORK_ERROR";
    } else if (error.message) {
      // Preservar mensajes de validación específicos
      mensaje = error.message;
    }

    const errorProcesado = new Error(mensaje);
    errorProcesado.codigo = codigo;
    errorProcesado.original = error;

    return errorProcesado;
  }

  /**
   * Sube la evidencia fotográfica asociada a un detalle de levantamiento
   * @param {FormData} formData - Datos del formulario que incluyen el archivo y la descripción
   * @returns {Promise<Object>} Respuesta del servidor
   */
  async subirEvidenciaLevantamiento(formData) {
    try {
      const tokens = this.obtenerTokens();

      const response = await fetch(`${this.baseURL}/dispositivos/api/detalles-levantamiento-evidencias/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "X-CSRFToken": tokens.csrf || "",
          ...(tokens.jwt ? { Authorization: `Bearer ${tokens.jwt}` } : {}),
        },
        body: formData,
      });

      const responseData = await this.procesarRespuesta(response);
      return responseData;
    } catch (error) {
      throw this.manejarError(error);
    }
  }
}

// Exportar instancia única del servicio
export default new DispositivosService();
